import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import { fetchStudentsForPrincipal, isStudentPrincipal } from '../../services/principalStudents';
import StudentRecaps from '../../components/Portal/StudentRecaps';
import { PageWrap, PageHead, Card, QuietState, studentDisplayName } from './customerUi';

/* Lesson Recaps — every recap a family may read, per child, newest first
   (Lesson Dashboard design pass 2, change 12, 2026-09-21). Reached from the
   Home card's "All recaps" and from More; Schedule → Past lessons keeps its own
   copy of the same list and is untouched.

   ONLY LESSONS WITH A RECAP ARE LISTED (Anthony, 2026-09-22). The design drew
   every past lesson, with "Nothing shared from this lesson" for the rest; that
   needs the child's lesson history from the schedule, and getting closures,
   flex days, moved lessons and makeups exactly right is the price of never
   telling a family a lesson happened that did not. The list is built from the
   recaps themselves, which is the read the Past lessons tab already does, so
   every row is a real lesson with something the teacher shared.

   The design's "Teacher notes only" row label is deliberately not built: it
   would tell a family a hidden note exists, which is what the wall is for.

   Students come from the same principal resolver as Home and Schedule, so a
   student account sees only itself, and `studentOnly` narrows the query to the
   one tier its rule admits. */

const LIST_INITIAL = 10;

const LessonRecaps = () => {
	const { userProfile } = useAuth();
	const familyId = userProfile?.familyId || null;
	const studentId = userProfile?.studentId || null;
	const role = userProfile?.role || null;
	const studentOnly = isStudentPrincipal(userProfile);

	const [students, setStudents] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	useEffect(() => {
		if (!familyId) {
			setLoading(false);
			return undefined;
		}
		let cancelled = false;
		(async () => {
			try {
				const kids = await fetchStudentsForPrincipal({ role, familyId, studentId });
				if (!cancelled) setStudents(kids);
			} catch (e) {
				console.error('Error loading students for recaps:', e);
				if (!cancelled) setError(true);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [role, familyId, studentId]);

	return (
		<PageWrap>
			<PageHead
				title='Lesson Recaps'
				subtitle={studentOnly
					? 'What your teachers shared after each lesson, newest first.'
					: 'What your teachers shared after each lesson, newest first. Notes marked For parents are for you.'}
			/>
			{loading ? (
				<Card><QuietState icon='fa-comment-lines'>Loading…</QuietState></Card>
			) : error ? (
				<Card>
					<QuietState icon='fa-triangle-exclamation'>
						We couldn't load your students just now. Please try again in a moment.
					</QuietState>
				</Card>
			) : !familyId || students.length === 0 ? (
				<Card>
					<QuietState icon='fa-comment-lines'>
						Lesson recaps show up here once your account is linked to a student.
					</QuietState>
				</Card>
			) : (
				students.map((student) => {
					const name = studentDisplayName(student);
					return (
						<Card key={student.id} style={{ marginBottom: 14, padding: '14px 16px 6px' }}>
							{(students.length > 1 || !studentOnly) && (
								<h2
									style={{
										fontFamily: 'var(--font-serif)',
										fontSize: '1.15rem',
										fontWeight: 'var(--fw-semibold, 600)',
										color: 'var(--text-heading, #384971)',
										margin: '0 0 0.4rem',
										textAlign: 'left',
									}}>
									{name}
								</h2>
							)}
							<StudentRecaps
								studentId={student.id}
								studentName={name}
								showHeading={false}
								studentOnly={studentOnly}
								initialCount={LIST_INITIAL}
								emptyMessage={studentOnly
									? "No lesson recaps yet. After a lesson, your teacher's recap will show up here."
									: `No lesson recaps for ${name} yet. After a lesson, your teacher's recap and any notes for parents will show up here.`}
							/>
						</Card>
					);
				})
			)}
		</PageWrap>
	);
};

export default LessonRecaps;
